import type { ReactNode } from 'react';

/**
 * The little of Markdown a chat answer actually uses.
 *
 * A model writes lists, bold labels and the occasional `identifier`, and HTML
 * collapses every newline it is given — which is why an answer listing sixteen
 * folders arrived as one run-on paragraph. This turns that text into real
 * elements.
 *
 * It is deliberately not a Markdown library: it builds React nodes rather than
 * HTML, so nothing a model writes — or a document it was fed — can be injected
 * into the page as markup.
 */

interface Block {
  kind: 'p' | 'ul' | 'ol' | 'table';
  lines: { text: string; depth: number }[];
}

const BULLET = /^(\s*)[-*•]\s+(.*)$/;
const NUMBER = /^(\s*)\d+[.)]\s+(.*)$/;

/** A Markdown table row: "| a | b |" or "a | b". */
const ROW = /^\s*\|?.*\|.*$/;

/** The |---|:---:| line under a table's header. */
const RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function cells(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

function parse(source: string): Block[] {
  const blocks: Block[] = [];

  for (const chunk of source.trim().split(/\n\s*\n/)) {
    const lines = chunk.split('\n');
    let current: Block | null = null;

    for (let n = 0; n < lines.length; n++) {
      const raw = lines[n];
      if (!raw.trim()) continue;

      // A table starts at a row followed by its |---| rule, and runs for as
      // long as rows keep coming. A lone line with a pipe in it stays text.
      if (ROW.test(raw) && RULE.test(lines[n + 1] ?? '')) {
        current = { kind: 'table', lines: [{ text: raw, depth: 0 }] };
        blocks.push(current);
        n += 1;
        while (n + 1 < lines.length && ROW.test(lines[n + 1]) && lines[n + 1].trim()) {
          current.lines.push({ text: lines[++n], depth: 0 });
        }
        current = null;
        continue;
      }

      const bullet = BULLET.exec(raw);
      const numbered = !bullet && NUMBER.exec(raw);
      const match = bullet ?? numbered;

      const kind: Block['kind'] = bullet ? 'ul' : numbered ? 'ol' : 'p';
      const text = match ? match[2] : raw.trim();

      // Two spaces of indent is one level in every style a model writes.
      const depth = match ? Math.min(3, Math.floor(match[1].length / 2)) : 0;

      if (!current || current.kind !== kind) {
        current = { kind, lines: [] };
        blocks.push(current);
      }
      current.lines.push({ text, depth });
    }
  }

  return blocks;
}

/** **bold**, *italic* and `code`, left as text when unmatched. */
function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;

  let last = 0;
  let match: RegExpExecArray | null;
  let n = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));

    const token = match[0];
    const id = `${key}-${n++}`;

    if (token.startsWith('**')) {
      out.push(<strong key={id}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      out.push(<code key={id}>{token.slice(1, -1)}</code>);
    } else {
      out.push(<em key={id}>{token.slice(1, -1)}</em>);
    }

    last = match.index + token.length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function RichText({ text }: { text: string }) {
  const blocks = parse(text);

  return (
    <>
      {blocks.map((block, i) => {
        if (block.kind === 'p') {
          return (
            <p key={i} className="rich-p">
              {block.lines.map((line, j) => (
                <span key={j}>
                  {j > 0 && <br />}
                  {inline(line.text, `${i}-${j}`)}
                </span>
              ))}
            </p>
          );
        }

        if (block.kind === 'table') {
          const [head, ...rows] = block.lines.map((l) => cells(l.text));
          // A cell that is a number — "37.75", "1,258,400", "-12.8" — lines
          // up on the right, as figures do in the reports themselves.
          const numeric = (c: string) => /^[-+]?[\d,.]+%?$/.test(c);

          return (
            <div key={i} className="rich-tablewrap">
              <table className="rich-table">
                <thead>
                  <tr>
                    {head.map((c, j) => (
                      <th key={j}>{inline(c, `${i}-h${j}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, r) => (
                    <tr key={r}>
                      {head.map((_, j) => (
                        <td
                          key={j}
                          className={
                            numeric(row[j] ?? '') ? 'is-num' : (row[j] ?? '').length <= 28 ? 'is-short' : undefined
                          }
                        >
                          {inline(row[j] ?? '', `${i}-${r}-${j}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        const List = block.kind === 'ul' ? 'ul' : 'ol';

        return (
          <List key={i} className="rich-list">
            {block.lines.map((line, j) => (
              <li key={j} style={line.depth ? { marginLeft: line.depth * 12 } : undefined}>
                {inline(line.text, `${i}-${j}`)}
              </li>
            ))}
          </List>
        );
      })}
    </>
  );
}
